import { useMemo } from 'react';
import {
  useAnnotationSolo,
  useIsComposing,
  useSelectedVersionId,
} from '@/features/comment-on-file';
import type { AnnotationShape } from '@/features/comment-on-file';

export interface RenderEntry {
  commentId: string;
  shape: AnnotationShape;
  color: string;
}

export type PersistedMode =
  | { kind: 'image' }
  | { kind: 'video'; currentTime: number }
  | { kind: 'pdf'; page: number };

// Seconds around the playhead within which a video annotation's shapes render
// — outside this window the underlying frame has changed too much.
const VIDEO_TIMESTAMP_WINDOW_SEC = 0.25;

/**
 * Resolves the single annotation the overlay should render, or `[]`.
 *
 * Rule: show only the annotation the user has soloed by clicking its comment
 * row, and only when nothing is actively being composed.
 *
 * Per-mode filtering:
 * - image: kind must match.
 * - video: kind + timestamp window.
 * - pdf:   kind + shape's own page must match this page.
 *
 * Version coupling: a soloed annotation only renders when its source version
 * matches the currently selected version — annotations are version-scoped, so
 * showing them on a different frame would be misleading.
 */
export function usePersistedEntries(mode: PersistedMode): RenderEntry[] {
  const { soloCommentId, soloAnnotation, soloVersionId } = useAnnotationSolo();
  const selectedVersionId = useSelectedVersionId();
  const isComposing = useIsComposing();

  return useMemo(() => {
    if (!soloCommentId || !soloAnnotation) return [];
    if (isComposing) return [];
    if (soloVersionId !== selectedVersionId) return [];
    if (soloAnnotation.kind !== mode.kind) return [];
    if (soloAnnotation.kind === 'video' && mode.kind === 'video') {
      if (Math.abs(soloAnnotation.timestamp - mode.currentTime) > VIDEO_TIMESTAMP_WINDOW_SEC) {
        return [];
      }
    }
    if (soloAnnotation.kind === 'pdf' && mode.kind === 'pdf') {
      const page = mode.page;
      return soloAnnotation.shapes
        .filter((s) => s.page === page)
        .map(({ shape, color }) => ({ commentId: soloCommentId, shape, color }));
    }
    return soloAnnotation.shapes.map(({ shape, color }) => ({
      commentId: soloCommentId,
      shape,
      color,
    }));
  }, [soloCommentId, soloAnnotation, soloVersionId, selectedVersionId, isComposing, mode]);
}
